import React from 'react';
import { Progress, Badge, Card } from 'antd';
import { FlexBox, TextCommon } from '@esg/ui';

interface OperationalCircleMetricProps {
    percent: number;
    color: string;
    icon: React.ReactNode;
    label: string;
    subLabel?: string;
    totalLabel: string;
    badgeKey1: string; // Fixed property name
    badgeVal1: number;
    badgeKey2: string;
    badgeVal2: number;
    status1?: "success" | "warning" | "error" | "default" | "processing";
    status2?: "success" | "warning" | "error" | "default" | "processing";
    circleWidth?: number;
}

export const OperationalCircleMetric: React.FC<OperationalCircleMetricProps> = ({ 
    percent, 
    color, 
    icon, 
    label, 
    subLabel = "Operational activity breakdown",
    totalLabel, 
    badgeKey1, 
    badgeVal1, 
    badgeKey2, 
    badgeVal2, 
    status1 = "success", 
    status2 = "default",
    circleWidth = 85,
}) => {
    
    return (
        <Card 
            size="small"
            style={{ 
                borderRadius: '10px', 
                border: '1px solid #e8e8e8',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                height: '100%',
            }}
            bodyStyle={{ padding: '20px' }}
        >
            <FlexBox direction="column" gap={16} width="100%">
                
                {/* Top Section: Icon, Title & Progress Ring */}
                <FlexBox direction="row" alignItems="center" justifyContent="space-between" width="100%">
                    <FlexBox direction="column" gap={4}>
                        <FlexBox direction="row" alignItems="center" gap={8}>
                            {React.cloneElement(icon as React.ReactElement, { 
                                style: { fontSize: '18px', color } 
                            })}
                            <TextCommon fontSize={15} fontWeight={600}>
                                {label}
                            </TextCommon>
                        </FlexBox>
                        <TextCommon fontSize={12} color="#8c8c8c">
                            {subLabel}
                        </TextCommon>
                    </FlexBox>

                    <Progress 
                        type="circle" 
                        percent={percent} 
                        width={circleWidth} 
                        strokeColor={color} 
                        strokeWidth={8}
                    />
                </FlexBox>

                {/* Middle Progress Bar */}
                <FlexBox direction="column" gap={4} width="100%">
                    <FlexBox direction="row" justifyContent="space-between" alignItems="center">
                        <TextCommon fontSize={13} fontWeight={600}>
                            {totalLabel}
                        </TextCommon>
                        <TextCommon fontSize={13} fontWeight={600} color={color}>
                            {percent}%
                        </TextCommon>
                    </FlexBox>
                    <Progress 
                        percent={percent} 
                        showInfo={false} 
                        strokeColor={color} 
                        size="small"
                    />
                </FlexBox>

                {/* Bottom Badges Row */}
                <FlexBox 
                    direction="row" 
                    alignItems="center" 
                    justifyContent="space-between" 
                    width="100%"
                >
                    {/* First Badge - Fixes missing "Paid" / "Active" / "Ongoing" text */}
                    <FlexBox direction="row" alignItems="center" gap={6}>
                        <Badge status={status1} />
                        <FlexBox direction="row" gap={4}>
                            <TextCommon fontSize={13}>{badgeKey1 || "Paid"}:</TextCommon>
                            <TextCommon fontSize={13} fontWeight={600}>{badgeVal1}</TextCommon>
                        </FlexBox>
                    </FlexBox>

                    {/* Second Badge */}
                    <FlexBox direction="row" alignItems="center" gap={6}>
                        <Badge status={status2} />
                        <FlexBox direction="row" gap={4}>
                            <TextCommon fontSize={13}>{badgeKey2}:</TextCommon>
                            <TextCommon fontSize={13} fontWeight={600}>{badgeVal2}</TextCommon>
                        </FlexBox>
                    </FlexBox>
                </FlexBox>

            </FlexBox>
        </Card>
    );
};